import React, { useEffect, useState } from 'react';
import { RxCross2 } from 'react-icons/rx';
import { MdEdit } from 'react-icons/md';
import { toast } from 'react-hot-toast';
import {
    assignNxtFund,
    createNxtFund,
    updateCampaignNtafId,
} from '~/services/campaign';

const NTAF_ID_PATTERN = /^[A-Za-z0-9]{1,10}$/;
import {
    fetchSkyApiFundCustomFields,
    fetchSkyApiFundDetails,
} from '~/services/finance-services';
import { lookupNxtFundByLookupId } from '~/services/sync';
import FundDetailsModal from './FundDetailsModal';
import { nxtFundsListUrl, nxtFundUrl } from '~/common/nxtUrl';

const normalizeName = (s: string | null | undefined) =>
    (s || '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();

interface Props {
    isOpen: boolean;
    onClose: () => void;
    campaign: any;
    patient: any;
    onLinked?: () => void | Promise<void>;
}

type View = 'details' | 'link' | 'create';

const ManageNxtFundModal: React.FC<Props> = ({
    isOpen,
    onClose,
    campaign,
    patient,
    onLinked,
}) => {
    const [unlinkedOverride, setUnlinkedOverride] = useState(false);

    useEffect(() => {
        if (!isOpen) setUnlinkedOverride(false);
    }, [isOpen]);

    const hasFund = !!campaign?.nxt_fund_id && !unlinkedOverride;

    if (hasFund) {
        return (
            <FundDetailsModal
                isOpen={isOpen}
                onClose={onClose}
                fundId={campaign.nxt_fund_id}
                onFetchFund={fetchSkyApiFundDetails}
                onFetchCustomFields={fetchSkyApiFundCustomFields}
                campaignId={campaign?.id ?? campaign?.campaign_id}
                currentNtafId={campaign?.ntaf_id}
                onNtafIdUpdated={() => onLinked?.()}
                onUnlinked={() => {
                    setUnlinkedOverride(true);
                    onLinked?.();
                }}
            />
        );
    }

    return (
        <LinkOrCreateModal
            isOpen={isOpen}
            onClose={onClose}
            campaign={campaign}
            patient={patient}
            onLinked={onLinked}
        />
    );
};

const LinkOrCreateModal: React.FC<{
    isOpen: boolean;
    onClose: () => void;
    campaign: any;
    patient: any;
    onLinked?: () => void | Promise<void>;
}> = ({ isOpen, onClose, campaign, patient, onLinked }) => {
    const [view, setView] = useState<View>('link');
    const [manualFundId, setManualFundId] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [actionError, setActionError] = useState<string | null>(null);

    const [createDescription, setCreateDescription] = useState('');
    const [createGoal, setCreateGoal] = useState<string>('');

    const [displayNtafId, setDisplayNtafId] = useState<string>('');
    const [editingNtafId, setEditingNtafId] = useState(false);
    const [ntafIdDraft, setNtafIdDraft] = useState('');
    const [savingNtafId, setSavingNtafId] = useState(false);
    const ntafIdInvalid =
        !!displayNtafId && !NTAF_ID_PATTERN.test(displayNtafId);
    const sanitizedNtafId = (displayNtafId || '')
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, '')
        .slice(0, 10);
    const ntafIdNeedsCleanup =
        ntafIdInvalid && !!sanitizedNtafId && sanitizedNtafId !== displayNtafId;
    const campaignId = campaign?.id ?? campaign?.campaign_id;

    const handleSaveNtafId = async () => {
        const next = ntafIdDraft.trim().toUpperCase();
        if (!NTAF_ID_PATTERN.test(next)) {
            toast.error('NTAF ID must be 1-10 alphanumeric characters.');
            return;
        }
        if (!campaignId) {
            toast.error('Missing campaign id; cannot update NTAF ID.');
            return;
        }
        if (next === (displayNtafId || '').toUpperCase()) {
            setEditingNtafId(false);
            return;
        }
        setSavingNtafId(true);
        try {
            const res = await updateCampaignNtafId(campaignId, next);
            setDisplayNtafId(res.ntaf_id);
            setEditingNtafId(false);
            toast.success('NTAF ID updated');
            await onLinked?.();
        } catch (err: any) {
            toast.error(err?.message || 'Failed to update NTAF ID');
        } finally {
            setSavingNtafId(false);
        }
    };

    const [cacheLookup, setCacheLookup] = useState<{
        status: 'idle' | 'loading' | 'hit' | 'miss' | 'error';
        fundId?: string;
        description?: string | null;
        inactive?: boolean | null;
    }>({ status: 'idle' });

    const patientFullName =
        [
            patient?.person?.first_name,
            patient?.person?.middle_name,
            patient?.person?.last_name,
        ]
            .filter(Boolean)
            .join(' ')
            .replace(/\s+/g, ' ')
            .trim() || '';

    const defaultDescription =
        patientFullName ||
        campaign?.campaign_name ||
        campaign?.fund_name ||
        campaign?.campaign_title ||
        '';

    useEffect(() => {
        if (!isOpen) {
            setView('link');
            setManualFundId('');
            setCreateDescription('');
            setCreateGoal('');
            setCacheLookup({ status: 'idle' });
            setDisplayNtafId('');
            setEditingNtafId(false);
            setNtafIdDraft('');
            setActionError(null);
            return;
        }

        setDisplayNtafId(campaign?.ntaf_id || '');
        setCreateDescription(defaultDescription || `Campaign ${campaign?.id ?? ''}`);
        setCreateGoal(
            campaign?.goal != null && campaign?.goal !== ''
                ? String(campaign.goal)
                : ''
        );

    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        const ntafId = displayNtafId;
        if (!ntafId) {
            setCacheLookup({ status: 'idle' });
            return;
        }

        let cancelled = false;
        setCacheLookup({ status: 'loading' });
        lookupNxtFundByLookupId(String(ntafId))
            .then((res) => {
                if (cancelled) return;
                if (res.fund_id != null && res.fund_id !== '') {
                    setCacheLookup({
                        status: 'hit',
                        fundId: String(res.fund_id),
                        description: res.description,
                        inactive: res.inactive,
                    });
                } else {
                    setCacheLookup({ status: 'miss' });
                }
            })
            .catch(() => {
                if (cancelled) return;
                setCacheLookup({ status: 'error' });
            });
        return () => {
            cancelled = true;
        };
    }, [isOpen, displayNtafId]);

    const handleAssign = async (fundId: string) => {
        if (!fundId.trim()) return;
        const targetCampaignId = campaign?.id ?? campaign?.campaign_id;
        console.log('[ManageNxtFundModal] handleAssign', {
            campaignId: targetCampaignId,
            fundId: fundId.trim(),
        });
        if (!targetCampaignId) {
            const msg = 'Missing campaign id; cannot assign NXT fund.';
            setActionError(msg);
            toast.error(msg);
            return;
        }
        setSubmitting(true);
        setActionError(null);
        try {
            await assignNxtFund(targetCampaignId, fundId.trim());
            toast.success('NXT fund linked');
            try {
                await onLinked?.();
            } catch (refreshErr: any) {
                console.error('[ManageNxtFundModal] onLinked refresh failed', refreshErr);
                setActionError(
                    `Fund linked, but refreshing campaign data failed: ${refreshErr?.message || refreshErr}`
                );
                return;
            }
            onClose();
        } catch (err: any) {
            console.error('[ManageNxtFundModal] handleAssign error', err);
            const msg = err?.message || 'Failed to link fund';
            setActionError(msg);
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    const handleCreate = async () => {
        const targetCampaignId = campaign?.id ?? campaign?.campaign_id;
        console.log('[ManageNxtFundModal] handleCreate', { campaignId: targetCampaignId });
        if (!targetCampaignId) {
            const msg = 'Missing campaign id; cannot create NXT fund.';
            setActionError(msg);
            toast.error(msg);
            return;
        }
        setSubmitting(true);
        setActionError(null);
        try {
            const overrides: { description?: string; goal?: number } = {};
            if (createDescription.trim()) {
                overrides.description = createDescription.trim();
            }
            if (createGoal.trim()) {
                const n = Number(createGoal);
                if (Number.isFinite(n)) overrides.goal = n;
            }
            await createNxtFund(targetCampaignId, overrides);
            toast.success('NXT fund created');
            try {
                await onLinked?.();
            } catch (refreshErr: any) {
                console.error('[ManageNxtFundModal] onLinked refresh failed', refreshErr);
                setActionError(
                    `Fund created, but refreshing campaign data failed: ${refreshErr?.message || refreshErr}`
                );
                return;
            }
            onClose();
        } catch (err: any) {
            console.error('[ManageNxtFundModal] handleCreate error', err);
            const msg = err?.message || 'Failed to create fund';
            setActionError(msg);
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div
            className='fixed inset-0 z-[99999] flex items-center justify-center bg-black bg-opacity-75'
            onClick={onClose}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className='relative w-full max-w-2xl rounded-lg bg-white shadow-lg'
            >
                <button
                    className='text-gray-600 hover:text-gray-900 absolute right-4 top-4 text-2xl'
                    onClick={onClose}
                >
                    <RxCross2 />
                </button>

                <div className='p-8'>
                    <h2 className='mb-4 text-2xl font-bold'>
                        {view === 'create' ? 'Create NXT Fund' : 'Link NXT Fund'}
                    </h2>

                    {actionError && (
                        <div className='mb-4 flex items-start justify-between gap-3 rounded-lg border-l-4 border-danger bg-meta-2 p-3 text-sm text-black'>
                            <div className='space-y-1'>
                                <div className='font-semibold'>
                                    Action failed
                                </div>
                                <div className='break-words font-mono text-xs'>
                                    {actionError}
                                </div>
                                <div className='text-xs opacity-75'>
                                    Check the browser console for the full
                                    request/response details.
                                </div>
                            </div>
                            <button
                                type='button'
                                onClick={() => setActionError(null)}
                                className='whitespace-nowrap rounded-full border-[2px] border-danger px-[12px] py-[2px] text-[11px]/[16px] font-bold uppercase text-danger transition-colors duration-500 hover:bg-danger hover:text-white'
                            >
                                Dismiss
                            </button>
                        </div>
                    )}

                    {view === 'link' && (
                        <>
                            <p className='mb-4 text-sm text-gray-600'>
                                NTAF ID:{' '}
                                <span
                                    className={`font-mono ${
                                        ntafIdInvalid ? 'text-danger' : ''
                                    }`}
                                >
                                    {displayNtafId || '—'}
                                </span>{' '}
                                · No NXT fund linked yet. Enter the fund ID to link an
                                existing fund, or create a new one.
                            </p>

                            <div className='mb-4 rounded-lg border-l-4 border-primary bg-meta-2 p-3 text-sm text-black'>
                                Look up funds by description in NXT here:{' '}
                                <a
                                    href={nxtFundsListUrl()}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className='font-medium text-primary underline hover:no-underline'
                                >
                                    Blackbaud NXT Funds
                                </a>
                                . Copy the fund ID from there and paste it below.
                            </div>

                            {cacheLookup.status === 'hit' && cacheLookup.fundId && (() => {
                                const cachedFundId = String(cacheLookup.fundId);
                                const campaignName =
                                    patientFullName ||
                                    campaign?.campaign_name ||
                                    '';
                                const nameMatches =
                                    !!cacheLookup.description &&
                                    !!campaignName &&
                                    normalizeName(cacheLookup.description) ===
                                        normalizeName(campaignName);
                                const containerClass = cacheLookup.inactive
                                    ? 'bg-meta-2 border-l-4 border-warning text-black'
                                    : 'bg-meta-2 border-l-4 border-success text-black';
                                const buttonClass = cacheLookup.inactive
                                    ? 'border-warning bg-warning text-black hover:bg-opacity-90'
                                    : 'border-success bg-success text-white hover:bg-opacity-90';
                                return (
                                    <div
                                        className={`mb-4 rounded-lg p-4 text-sm ${containerClass}`}
                                    >
                                        <div className='mb-3 space-y-1'>
                                            <div>
                                                Cached match for NTAF{' '}
                                                <span className='font-mono'>
                                                    {displayNtafId}
                                                </span>
                                                : fund ID{' '}
                                                <a
                                                    href={nxtFundUrl(cachedFundId)}
                                                    target='_blank'
                                                    rel='noopener noreferrer'
                                                    className='font-mono font-semibold underline hover:no-underline'
                                                >
                                                    {cachedFundId}
                                                </a>
                                                {cacheLookup.inactive && (
                                                    <span className='ml-2 rounded bg-warning px-1.5 py-0.5 text-[10px] font-bold uppercase text-black'>
                                                        Inactive
                                                    </span>
                                                )}
                                            </div>
                                            {cacheLookup.description && (
                                                <div className='text-xs'>
                                                    Fund description:{' '}
                                                    <span className='font-medium'>
                                                        {cacheLookup.description}
                                                    </span>
                                                    {campaignName && (
                                                        <>
                                                            {' '}
                                                            vs. campaign{' '}
                                                            <span className='font-medium'>
                                                                {campaignName}
                                                            </span>{' '}
                                                            {nameMatches ? (
                                                                <span title='Names match'>
                                                                    ✅
                                                                </span>
                                                            ) : (
                                                                <span title='Names do not match — verify in NXT'>
                                                                    ⚠️
                                                                </span>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                            <div className='text-xs opacity-75'>
                                                <a
                                                    href={nxtFundUrl(cachedFundId)}
                                                    target='_blank'
                                                    rel='noopener noreferrer'
                                                    className='underline hover:no-underline'
                                                >
                                                    Verify in NXT
                                                </a>{' '}
                                                before assigning.
                                            </div>
                                        </div>
                                        <button
                                            type='button'
                                            onClick={() => {
                                                console.log(
                                                    '[ManageNxtFundModal] "Use this fund" clicked — assigning',
                                                    cachedFundId
                                                );
                                                handleAssign(cachedFundId);
                                            }}
                                            disabled={submitting}
                                            className={`w-full whitespace-nowrap rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 disabled:opacity-50 ${buttonClass}`}
                                        >
                                            {submitting
                                                ? 'Assigning…'
                                                : `Use this fund (${cachedFundId})`}
                                        </button>
                                    </div>
                                );
                            })()}
                            {cacheLookup.status === 'miss' && displayNtafId && (
                                <div className='mb-4 rounded-lg bg-gray-50 p-3 text-xs text-gray-600'>
                                    No cached fund found for NTAF{' '}
                                    <span className='font-mono'>
                                        {displayNtafId}
                                    </span>
                                    .
                                </div>
                            )}

                            <div className='mb-6'>
                                <label className='mb-1 block text-sm font-medium text-gray-600'>
                                    {cacheLookup.status === 'hit'
                                        ? 'Or assign a different fund ID'
                                        : 'NXT Fund ID'}
                                </label>
                                <div className='flex gap-2'>
                                    <input
                                        type='text'
                                        value={manualFundId}
                                        onChange={(e) =>
                                            setManualFundId(e.target.value)
                                        }
                                        placeholder='e.g. 14153'
                                        className='flex-1 rounded border border-stroke px-3 py-2 text-sm font-mono focus:border-primary focus:outline-none'
                                    />
                                    <button
                                        type='button'
                                        onClick={() => handleAssign(manualFundId)}
                                        disabled={
                                            submitting ||
                                            !String(manualFundId ?? '').trim()
                                        }
                                        className='rounded-full border-[2px] border-primary bg-primary px-[20px] py-[5px] text-[12px]/[16px] font-bold uppercase text-white transition-colors duration-500 hover:bg-opacity-90 disabled:opacity-50'
                                    >
                                        Assign
                                    </button>
                                </div>
                            </div>

                            <div className='mt-6 flex justify-end gap-3 border-t pt-4'>
                                <button
                                    type='button'
                                    onClick={onClose}
                                    className='rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-black hover:bg-black hover:text-white'
                                >
                                    Cancel
                                </button>
                                <button
                                    type='button'
                                    onClick={() => setView('create')}
                                    className={
                                        cacheLookup.status === 'hit'
                                            ? 'rounded-full border-[2px] border-stroke px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase text-gray-600 transition-colors duration-500 hover:border-primary hover:text-primary'
                                            : 'rounded-full border-[2px] border-primary bg-primary px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase text-white transition-colors duration-500 hover:bg-opacity-90'
                                    }
                                >
                                    Create new fund in NXT
                                </button>
                            </div>
                        </>
                    )}

                    {view === 'create' && (
                        <>
                            <p className='mb-4 text-sm text-gray-600'>
                                A new fund will be created in NXT and linked to this
                                campaign. Most fields are auto-derived from the campaign
                                — you can override description and goal below.
                            </p>

                            <div className='space-y-4'>
                                <div>
                                    <label className='mb-1 block text-sm font-medium text-gray-600'>
                                        NTAF ID (lookup_id)
                                    </label>
                                    {editingNtafId ? (
                                        <div className='flex items-center gap-2'>
                                            <input
                                                type='text'
                                                value={ntafIdDraft}
                                                onChange={(e) =>
                                                    setNtafIdDraft(e.target.value)
                                                }
                                                maxLength={10}
                                                placeholder='Alphanumeric, max 10'
                                                className='flex-1 rounded border border-stroke px-3 py-2 text-sm font-mono uppercase focus:border-primary focus:outline-none'
                                                autoFocus
                                            />
                                            <button
                                                type='button'
                                                onClick={handleSaveNtafId}
                                                disabled={savingNtafId}
                                                className='rounded-full border-[2px] border-primary bg-primary px-[16px] py-[4px] text-[12px]/[16px] font-bold uppercase text-white transition-colors duration-500 hover:bg-opacity-90 disabled:opacity-50'
                                            >
                                                {savingNtafId ? 'Saving…' : 'Save'}
                                            </button>
                                            <button
                                                type='button'
                                                onClick={() => {
                                                    setEditingNtafId(false);
                                                    setNtafIdDraft('');
                                                }}
                                                disabled={savingNtafId}
                                                className='rounded-full border-[2px] px-[16px] py-[4px] text-[12px]/[16px] font-bold uppercase transition-colors duration-500 hover:border-black hover:bg-black hover:text-white disabled:opacity-50'
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    ) : (
                                        <div className='flex items-center gap-2'>
                                            <span className='text-base font-mono'>
                                                {sanitizedNtafId ||
                                                    displayNtafId ||
                                                    'N/A'}
                                            </span>
                                            <button
                                                type='button'
                                                onClick={() => {
                                                    setNtafIdDraft(
                                                        ntafIdNeedsCleanup
                                                            ? sanitizedNtafId
                                                            : displayNtafId || ''
                                                    );
                                                    setEditingNtafId(true);
                                                }}
                                                className='p-1 rounded text-primary transition-colors hover:bg-gray-100'
                                                title='Edit NTAF ID'
                                            >
                                                <MdEdit size={16} />
                                            </button>
                                        </div>
                                    )}
                                    {ntafIdNeedsCleanup && !editingNtafId && (
                                        <p className='mt-1 text-xs text-gray-500'>
                                            Auto-cleaned from{' '}
                                            <span className='font-mono'>
                                                {displayNtafId}
                                            </span>{' '}
                                            (alphanumeric only). The cleaned
                                            value will be saved when the fund is
                                            created.
                                        </p>
                                    )}
                                </div>
                                <ReadOnlyRow
                                    label='Fund Type'
                                    value={
                                        campaign?.campaign_type?.code === 'compassion'
                                            ? 'Restricted - Non Patient'
                                            : campaign?.campaign_type?.name ||
                                              '(derived from campaign type)'
                                    }
                                />
                                <ReadOnlyRow
                                    label='Region Code'
                                    value={
                                        campaign?.campaign_type?.code === 'compassion'
                                            ? '12'
                                            : '(derived from patient state)'
                                    }
                                />

                                <div>
                                    <label className='mb-1 block text-sm font-medium text-gray-600'>
                                        Description
                                    </label>
                                    <input
                                        type='text'
                                        value={createDescription}
                                        onChange={(e) =>
                                            setCreateDescription(e.target.value)
                                        }
                                        className='w-full rounded border border-stroke px-3 py-2 text-sm focus:border-primary focus:outline-none'
                                    />
                                    <p className='mt-1 text-xs text-gray-500'>
                                        Campaign ID ({campaign?.id}) is appended
                                        automatically for uniqueness.
                                    </p>
                                </div>

                                <div>
                                    <label className='mb-1 block text-sm font-medium text-gray-600'>
                                        Goal (USD)
                                    </label>
                                    <input
                                        type='number'
                                        min='0'
                                        step='1'
                                        value={createGoal}
                                        onChange={(e) => setCreateGoal(e.target.value)}
                                        placeholder='Optional'
                                        className='w-full rounded border border-stroke px-3 py-2 text-sm focus:border-primary focus:outline-none'
                                    />
                                </div>
                            </div>

                            <div className='mt-8 flex justify-end gap-3 border-t pt-4'>
                                <button
                                    type='button'
                                    onClick={() => setView('link')}
                                    disabled={submitting}
                                    className='rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-black hover:bg-black hover:text-white disabled:opacity-50'
                                >
                                    Back
                                </button>
                                <button
                                    type='button'
                                    onClick={handleCreate}
                                    disabled={submitting}
                                    className='rounded-full border-[2px] border-primary bg-primary px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase text-white transition-colors duration-500 hover:bg-opacity-90 disabled:opacity-50'
                                >
                                    {submitting ? 'Creating…' : 'Create Fund'}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

const ReadOnlyRow: React.FC<{ label: string; value: any }> = ({ label, value }) => (
    <div>
        <label className='mb-1 block text-sm font-medium text-gray-600'>{label}</label>
        <div className='text-base'>{value || 'N/A'}</div>
    </div>
);

export default ManageNxtFundModal;
